import { motion } from "framer-motion";
import { Compass, GraduationCap, Repeat, HeartHandshake, ArrowRight, Infinity as InfinityIcon } from "lucide-react";

const PILLARS = [
  { icon: Compass, t: "Autoconhecimento", d: "Conhecer-se para crescer: personalidade, valores e propósito." },
  { icon: GraduationCap, t: "Auto-educação", d: "Aprendizagem contínua, ao ritmo de cada um, rumo à excelência." },
  { icon: Repeat, t: "Hábitos & Cultura", d: "Comportamentos que se enraízam e reforçam a cultura da equipa." },
  { icon: HeartHandshake, t: "Alianças", d: "Relações e colaboração que multiplicam o valor no tempo." },
];

const fade = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

export function EroiSection({ variant = "portal" }) {
  const portal = variant === "portal";
  const th = portal
    ? {
        sec: "bg-[#070B14]",
        eyebrow: "text-[#F7861F]",
        h2: "font-display font-semibold text-3xl md:text-4xl tracking-tight text-white",
        sub: "text-white/60",
        roiCard: "rounded-2xl border border-white/10 bg-[#0C1222]",
        eroiCard: "rounded-2xl border border-[#F7861F]/40 bg-[#121A30] shadow-[0_0_40px_-12px_rgba(247,134,31,0.5)]",
        card: "rounded-2xl bg-[#0C1222] border border-white/10",
        iconWrapBlue: "bg-[#1A4FCC]/15", iconBlue: "text-[#3B6EFF]",
        iconWrapOrange: "bg-[#F7861F]/15", iconOrange: "text-[#F7861F]",
        muted: "text-white/55", label: "text-white/40",
        arrow: "text-white/30", moteH: "font-display", moteMuted: "text-white/50",
      }
    : {
        sec: "bg-[#0a0b0e] border-y border-brand-border",
        eyebrow: "text-brand-orange",
        h2: "font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight",
        sub: "text-brand-muted",
        roiCard: "rounded-sm border border-brand-border bg-brand-surface",
        eroiCard: "rounded-sm border border-brand-orange/40 bg-brand-orange/5 glow-orange",
        card: "rounded-sm bg-brand-surface border border-brand-border",
        iconWrapBlue: "bg-brand-blue/15", iconBlue: "text-brand-blue",
        iconWrapOrange: "bg-brand-orange/15", iconOrange: "text-brand-orange",
        muted: "text-brand-muted", label: "text-brand-muted",
        arrow: "text-brand-muted", moteH: "font-heading", moteMuted: "text-brand-muted",
      };
  const cardRadius = portal ? "rounded-2xl" : "rounded-sm";

  return (
    <section id="eroi" className={`py-24 px-5 scroll-mt-16 ${th.sec}`} data-testid="eroi-section">
      <div className="max-w-6xl mx-auto">
        <div className="max-w-3xl">
          <span className={`text-xs tracking-[0.24em] uppercase font-semibold ${th.eyebrow}`}>A nossa filosofia</span>
          <h2 className={`mt-3 ${th.h2}`}>Do ROI ao EROI</h2>
          <p className={`mt-4 text-lg leading-relaxed ${th.sub}`}>
            O retorno tradicional (ROI) mede o ganho imediato. Nós acreditamos no{" "}
            <strong className={portal ? "text-white" : "text-white"}>EROI — Eternal Return on Investment</strong>:
            o retorno que perdura e se compõe quando investimos nas pessoas — no seu autoconhecimento, na sua
            auto-educação e na sua missão.
          </p>
        </div>

        {/* ROI -> EROI */}
        <div className="mt-10 grid md:grid-cols-[1fr_auto_1fr] gap-4 md:gap-6 items-stretch">
          <div className={`${th.roiCard} p-7`}>
            <p className={`text-xs uppercase tracking-[0.2em] font-bold ${th.label}`}>ROI</p>
            <p className={`${portal ? "font-display" : "font-heading"} font-bold text-xl mt-1`}>Return on Investment</p>
            <p className={`text-sm mt-3 leading-relaxed ${th.muted}`}>Retorno imediato e financeiro de cada transação. Termina quando o negócio fecha.</p>
          </div>
          <div className="flex md:flex-col items-center justify-center">
            <ArrowRight className={`${th.arrow} rotate-90 md:rotate-0`} size={26} />
          </div>
          <div className={`${th.eroiCard} p-7 relative`}>
            <div className="flex items-center justify-between">
              <p className={`text-xs uppercase tracking-[0.2em] font-bold ${th.iconOrange}`}>EROI</p>
              <InfinityIcon size={20} className={th.iconOrange} />
            </div>
            <p className={`${portal ? "font-display" : "font-heading"} font-bold text-xl mt-1`}>Eternal Return on Investment</p>
            <p className={`text-sm mt-3 leading-relaxed ${th.muted}`}>Retorno duradouro e composto de investir nas pessoas. Cresce muito para além do negócio.</p>
          </div>
        </div>

        {/* Pilares */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          {PILLARS.map((p, i) => (
            <motion.div key={p.t} custom={i} initial="hidden" whileInView="show" viewport={{ once: true }} variants={fade}
              className={`${th.card} p-6`} data-testid={`eroi-pillar-${i}`}>
              <div className={`h-11 w-11 ${cardRadius} flex items-center justify-center ${th.iconWrapBlue}`}>
                <p.icon size={20} className={th.iconBlue} />
              </div>
              <p className={`${portal ? "font-display" : "font-heading"} font-semibold text-lg mt-4`}>{p.t}</p>
              <p className={`text-sm mt-2 leading-relaxed ${th.muted}`}>{p.d}</p>
            </motion.div>
          ))}
        </div>

        {/* Mote */}
        <div className="mt-12 text-center">
          <p className={`${th.moteH} font-bold text-2xl sm:text-3xl`}>
            Do <span className={th.moteMuted}>ROI</span> ao <span className={th.iconOrange}>EROI</span>
          </p>
          <p className={`mt-2 text-xs uppercase tracking-[0.28em] ${th.moteMuted}`}>Eternal Return on Investment</p>
        </div>
      </div>
    </section>
  );
}
